import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Box, Grid} from '@mui/material';
import './ViewCategorization.scss';
import CategorizationHeader from '../../components/categorization/header/CategorizationHeader';
import CategorizationStep1, {
  options,
} from '../categorization/step-1/CategorizationStep1';
import Corpus from './corpus';
import {FolderTreeItem} from '../../api/endpoints/corpusApi';
import CategorizationViewResults from './categorization-view-results/CategorizationViewResults';
import {
  CategorizationProviewResponse,
  IAnalysisContainer,
} from '../../api/types/categorization.type';
import {ISelectItem} from '../../shared/types';
import {setsAreEqual} from './utils';
import {RootState} from '../../redux/store';
import {useDispatch, useSelector} from 'react-redux';
import {clearCategorizationState} from '../../redux/slice/categorizationSlice';

export type CorpusFilterState = {
  folder: FolderTreeItem[];
  assignment: FolderTreeItem[];
  savedSearch: FolderTreeItem[];
  production: FolderTreeItem[];
};

type CategorizationContextType = {
  cohesion: number;
  setCohesion: (newValue: number) => void;
  corpusFilter: CorpusFilterState;
  setCorpusFilter: (dispatch: React.SetStateAction<CorpusFilterState>) => void;
  analysisSetContexts: IAnalysisContainer[] | null;
};
export type TrainingSet = {
  ids: string[];
  trainingSet: ISelectItem | null;
};
export const CorpusContext = createContext<CategorizationContextType | null>(null);

export const useCorpusContext = () => {
  const ctx = useContext(CorpusContext);
  if (!ctx) {
    throw new Error('useCategorizationContext must be used within a Provider');
  }
  return ctx;
};
const initialCorpus: CorpusFilterState = {
  folder: [],
  assignment: [],
  savedSearch: [],
  production: [],
};

const ViewCategorization: React.FC = () => {
  const {
    proviewProductionDetails,
    trainingSetType,
    trainingSetName,
    corpusDetails,
  } = useSelector((state: RootState) => state?.categorization);
  const [cohesion, setCohesion] = useState(70);

  const [selectedTrainingSet, setSelectedTrainingSet] =
    useState<TrainingSet | null>(
      proviewProductionDetails?.productionId
        ? {
            ids: [proviewProductionDetails.productionId],
            trainingSet: {
              id: 6,
              name: trainingSetName || '',
              trainingSetType: trainingSetType || '',
            },
          }
        : null
    );
  const [corpusFilter, setCorpusFilter] = useState<CorpusFilterState>(
    corpusDetails
      ? {...initialCorpus, [corpusDetails.type]: [corpusDetails]}
      : initialCorpus
  );
  const [categorizationViewData, setCategorizationViewData] =
    useState<CategorizationProviewResponse | null>(null);
  const [from, setFrom] = useState<'client' | 'server'>('client');

  const oldCorpus = useRef<CorpusFilterState>(null);

  const dispatch = useDispatch();

  useEffect(() => {
    if (categorizationViewData?.proview) {
      const trainingIds =
        categorizationViewData?.proview?.trainingSetId.map((id) =>
          id.toString()
        ) || [];
      const newOptions = [
        ...options,
        categorizationViewData?.proview?.proviewProductionDetails
          ?.isProductionExternalSource
          ? {
              id: 6,
              name: 'Production Guard Source',
              trainingSetType:
                categorizationViewData?.proview?.trainingSetType || '',
            }
          : null,
      ];
      const trainingType =
        newOptions.find(
          (op) =>
            op?.trainingSetType ===
            categorizationViewData?.proview?.trainingSetType
        ) || null;
      setSelectedTrainingSet({
        ids: trainingIds || [],
        trainingSet: trainingType,
      });
      setCohesion(categorizationViewData?.proview?.cohesionLevel * 100 || 0);
      //set Corpus Filter
      let newCorpus = {...corpusFilter};
      categorizationViewData.proview.analysisContainer?.analysisSetContexts.forEach(
        (context) => {
          newCorpus = {
            ...newCorpus,
            [context.type]: context.contextIds.map((id) => ({
              id: id.toString(),
            })),
          };
        }
      );
      oldCorpus.current = newCorpus;
      setCorpusFilter(newCorpus);
      setFrom('server');
    }
  }, [categorizationViewData?.proview]);
  useEffect(() => {
    if (categorizationViewData?.proview) {
      //check and compare cohesion
      if (categorizationViewData.proview.cohesionLevel != cohesion / 100) {
        setFrom('client');
        return;
      }

      //check and compare selectedTrainingSet
      if (
        selectedTrainingSet?.trainingSet?.trainingSetType !==
        categorizationViewData.proview.trainingSetType
      ) {
        setFrom('client');
        return;
      }
      const trainingSetIdSet = new Set(
        categorizationViewData?.proview?.trainingSetId.map((id) =>
          id.toString()
        ) || []
      );
      const selectedTrainingSetIdSet = new Set(selectedTrainingSet?.ids || []);
      if (setsAreEqual(trainingSetIdSet, selectedTrainingSetIdSet) === false) {
        setFrom('client');
        return;
      }

      //check and compare corpusFilter
      for (let key in corpusFilter) {
        const set1 = new Set(
          corpusFilter[key as keyof CorpusFilterState].map((item) =>
            item.id.toString()
          )
        );
        const set2 = new Set(
          oldCorpus.current
            ? oldCorpus.current[key as keyof CorpusFilterState].map((item) =>
                item.id.toString()
              )
            : []
        );

        if (setsAreEqual(set1, set2) === false) {
          setFrom('client');
          return;
        }
      }
      setFrom('server');
    }
  }, [cohesion, corpusFilter, selectedTrainingSet]);

  //clear store if navigating away from categorization view
  useEffect(() => {
    return () => {
      const currentPath = window.location.pathname.toLowerCase();
      const categorizationPath = '/Proview/Proview';
      const docListPath = 'Document/DocList';

      if (
        currentPath.includes(categorizationPath.toLowerCase()) ||
        currentPath.includes(docListPath.toLowerCase())
      )
        return;

      dispatch(clearCategorizationState());
    };
  }, []);
  const analysisSetContexts = useMemo(() => {
    if (categorizationViewData?.proview) {
      return categorizationViewData?.proview?.analysisContainer
        ?.analysisSetContexts;
    }
    if (corpusDetails) {
      return [
        {
          type: corpusDetails.type,
          contextIds: [Number(corpusDetails.id)],
          contextIdText: [corpusDetails.text],
        } as IAnalysisContainer,
      ];
    }
  }, [corpusDetails, categorizationViewData]);
  return (
    <Box className="view-Categorization" data-testid="vc-view-Categorization">
      <CategorizationHeader />
      <Grid container sx={{height: '100%'}}>
        <Grid size={4} className="grid-container">
          <CategorizationStep1
            selectedTrainingSet={selectedTrainingSet}
            setSelectedTrainingSet={setSelectedTrainingSet}
          />
        </Grid>
        <Grid size={4} className="grid-container">
          <CorpusContext.Provider
            value={{
              cohesion,
              setCohesion,
              corpusFilter,
              setCorpusFilter,
              analysisSetContexts: analysisSetContexts ?? null,
            }}
          >
            <Corpus />
          </CorpusContext.Provider>
        </Grid>
        <Grid size={4}>
          <CategorizationViewResults
            cohesion={cohesion}
            corpusFilter={corpusFilter}
            selectedTrainingSet={selectedTrainingSet}
            categorizationViewData={categorizationViewData}
            setCategorizationViewData={setCategorizationViewData}
            from={from}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ViewCategorization;
